// TaskList — the action hub: prioritized pending decisions with
// direct actions (FC 25 Task List pattern).

import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useClubTasks, type ClubTask } from '@/hooks/useClubTasks'
import { cardItem, staggerContainer, useMotionVariants } from '../../motion/motionTokens'
import {
  Wrench, Trophy, Shield, Users, Zap, Target, Frown, Siren,
  ArrowRight, ClipboardCheck, type LucideIcon,
} from 'lucide-react'

const TASK_ICONS: Record<string, LucideIcon> = {
  wrench: Wrench,
  trophy: Trophy,
  shield: Shield,
  users: Users,
  zap: Zap,
  target: Target,
  frown: Frown,
  siren: Siren,
}

function priorityClass(priority: ClubTask['priority']): string {
  return priority === 1 ? 'priority-high' : priority === 2 ? 'priority-med' : 'priority-low'
}

export function TaskList() {
  const tasks = useClubTasks()
  const navigate = useNavigate()
  const container = useMotionVariants(staggerContainer)

  return (
    <div>
      <div className="section-label" style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
        <ClipboardCheck size={13} />
        Decisiones pendientes {tasks.length > 0 && `(${tasks.length})`}
      </div>

      {tasks.length === 0 ? (
        <div className="empty-state small" style={{ fontStyle: 'normal', textAlign: 'left', padding: '0.75rem' }}>
          Todo en orden: el club funciona solo. Pasa por <strong>Mi Club</strong> para las tareas del dia.
        </div>
      ) : (
        <motion.div
          className="space-y-2"
          variants={container}
          initial="initial"
          animate="enter"
        >
          {tasks.map(task => {
            const Icon = TASK_ICONS[task.icon] ?? Zap
            return (
              <motion.div key={task.id} variants={cardItem} className={`task-card ${priorityClass(task.priority)}`}>
                <Icon size={18} style={{ flexShrink: 0, color: 'var(--text-secondary)' }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: '0.8rem', color: 'var(--text-strong)' }}>
                    {task.title}
                  </div>
                  <div className="meta-text">{task.detail}</div>
                </div>
                {task.action && (
                  <button
                    className="task-action"
                    disabled={task.action.disabled}
                    onClick={task.action.run}
                  >
                    {task.action.label}
                  </button>
                )}
                {!task.action && task.link && (
                  <button
                    className="icon-btn"
                    style={{ fontSize: '0.72rem', fontWeight: 600, whiteSpace: 'nowrap' }}
                    onClick={() => navigate(task.link!.to)}
                  >
                    {task.link.label} <ArrowRight size={12} />
                  </button>
                )}
              </motion.div>
            )
          })}
        </motion.div>
      )}
    </div>
  )
}
